import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faTrashCan, faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { convertToImageUrl, addCacheBuster } from '../../utils/imageUrlHelper';

const resolveObjectDisplayValue = (value) => {
  const preferredKeys = ['displayName', 'name', 'label', 'title', 'description'];

  for (const key of preferredKeys) {
    const candidate = value[key];
    if (candidate !== undefined && candidate !== null && candidate !== '') {
      return String(candidate);
    }
  }

  const nameLikeKey = Object.keys(value).find((key) => /name$/i.test(key));
  if (nameLikeKey) {
    const candidate = value[nameLikeKey];
    if (candidate !== undefined && candidate !== null && candidate !== '') {
      return String(candidate);
    }
  }

  const firstPrimitive = Object.entries(value).find(([_, candidate]) =>
    ['string', 'number', 'boolean'].includes(typeof candidate)
  );
  if (firstPrimitive) {
    return String(firstPrimitive[1]);
  }

  return JSON.stringify(value);
};

/**
 * Texto truncado en grid con popover al hover para ver el contenido completo (logs, notas, etc.)
 * Se posiciona en fixed sobre la celda (mismo nivel de fila/columna), sin abrirse debajo.
 */
const ExpandableTextCell = ({ text, maxLength = 80 }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState(null);
  const triggerRef = useRef(null);
  const fullText = text == null ? '' : String(text);

  const updatePopoverPosition = () => {
    if (!triggerRef.current) {
      return;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    const maxWidth = Math.min(420, window.innerWidth - 24);
    let left = rect.left;
    if (left + maxWidth > window.innerWidth - 12) {
      left = Math.max(12, window.innerWidth - maxWidth - 12);
    }
    setPopoverStyle({
      top: Math.max(8, rect.top),
      left,
      maxWidth,
    });
  };

  const handleOpen = () => {
    updatePopoverPosition();
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  if (!fullText) {
    return '';
  }

  const needsExpand = fullText.length > maxLength;
  const preview = needsExpand ? `${fullText.substring(0, maxLength)}...` : fullText;

  if (!needsExpand) {
    return <span className="grid-expandable-text-preview">{preview}</span>;
  }

  return (
    <div
      ref={triggerRef}
      className={`grid-expandable-text ${isOpen ? 'is-open' : ''}`}
      onMouseEnter={handleOpen}
      onMouseLeave={handleClose}
      onFocus={handleOpen}
      onBlur={handleClose}
      tabIndex={0}
    >
      <span className="grid-expandable-text-preview">{preview}</span>
      {isOpen && popoverStyle && createPortal(
        <div
          className="grid-expandable-text-popover"
          role="tooltip"
          style={popoverStyle}
          onMouseEnter={handleOpen}
          onMouseLeave={handleClose}
        >
          <pre className="grid-expandable-text-content">{fullText}</pre>
        </div>,
        document.body
      )}
    </div>
  );
};

/**
 * Renderiza automáticamente el valor de una celda
 */
const autoRenderCell = (row, column) => {
  const value = row[column.key];
  
  // Si hay render personalizado, usarlo
  if (column.render) {
    return column.render(row);
  }
  
  // Detectar tipo del campo
  const fieldType = column.type ?? column.formConfig?.type;
  
  const accept = column.accept ?? column.formConfig?.accept;
  const isImageFile = fieldType === 'file' && accept && String(accept).toLowerCase().split(',').some((token) => {
    const valueToken = token.trim();
    return valueToken === 'image' || valueToken === 'image/*' || valueToken.startsWith('image/');
  });

  // type: 'file' + accept imagen => preview en grid
  if (isImageFile && value) {
    const imageUrl = addCacheBuster(convertToImageUrl(value, ''));
    if (!imageUrl) {
      return '';
    }
    return (
      <img 
        src={imageUrl}
        alt="" 
        className="preview-image-grid" 
      />
    );
  }

  // type: 'file' no imagen => nombre del fichero
  if (fieldType === 'file' && value) {
    if (typeof value === 'string') {
      const normalized = value.replace(/\\/g, '/');
      const parts = normalized.split('/');
      return parts[parts.length - 1] || value;
    }
    if (typeof File !== 'undefined' && value instanceof File) {
      return value.name;
    }
  }

  // type: 'textarea' => preview truncado + popover con texto completo
  if (fieldType === 'textarea') {
    const maxLength = column.maxLengthInGrid ?? column.formConfig?.maxLengthInGrid ?? 80;
    return <ExpandableTextCell text={value} maxLength={maxLength} />;
  }
  
  // Renderizado automático según tipo
  if (value === null || value === undefined) {
    return '';
  }
  
  // Si es un objeto, resolver de forma genérica
  if (typeof value === 'object' && value !== null) {
    return resolveObjectDisplayValue(value);
  }
  
  // Si es booleano
  if (typeof value === 'boolean') {
    return value ? '✓' : '✗';
  }
  
  // Si es número con type 'number', formatear
  const decimals = column.decimals ?? column.formConfig?.decimals;
  if (typeof value === 'number' && fieldType === 'number') {
    return value.toFixed(decimals || 2);
  }
  
  // Si es string y tiene maxLengthInGrid, truncar
  const maxLength = column.maxLengthInGrid ?? column.formConfig?.maxLengthInGrid;
  if (typeof value === 'string' && maxLength && value.length > maxLength) {
    return value.substring(0, maxLength) + '...';
  }
  
  // Por defecto, convertir a string
  return String(value);
};

/**
 * Construye una paginación compacta:
 * 1 ... 6 7 8 ... 20
 */
const buildCompactPagination = (totalPages, currentPage, maxVisiblePages = 7) => {
  const pages = [];
  const lastPage = totalPages - 1;

  if (totalPages <= maxVisiblePages) {
    return Array.from({ length: totalPages }, (_, index) => index);
  }

  const innerWindow = Math.max(3, maxVisiblePages - 2);
  let start = Math.max(1, currentPage - Math.floor(innerWindow / 2));
  let end = Math.min(lastPage - 1, start + innerWindow - 1);
  start = Math.max(1, end - innerWindow + 1);

  pages.push(0);

  if (start > 1) {
    pages.push('ellipsis-start');
  }

  for (let page = start; page <= end; page += 1) {
    pages.push(page);
  }

  if (end < lastPage - 1) {
    pages.push('ellipsis-end');
  }

  pages.push(lastPage);
  return pages;
};

/**
 * DataGrid reutilizable con sorting, paginación y acciones
 * 
 * @param {Array} columns - Definición de columnas
 *   [{
 *     key: 'name',
 *     label: 'Name',
 *     sortable: true,
 *     render: (row) => row.name,  // OPCIONAL: si no se especifica, usa autoRenderCell
 *     className: 'text-center',
 *     formConfig: { type: 'number', decimals: 2 }  // Para formateo automático
 *   }]
 * 
 * Renderizado automático:
 * - Campos simples: muestra el valor directamente
 * - Objetos: intenta acceder a .description o .name
 * - Números con formConfig.type='number': formatea con decimales
 * - Booleanos: muestra ✓ o ✗
 * - null/undefined: muestra ''
 * 
 * @param {Array} data - Datos a mostrar
 * @param {Object} sortConfig - { key: 'name', direction: 'ascending' }
 * @param {Function} onSort - Callback para ordenar
 * @param {Function} onEdit - Callback para editar (row)
 * @param {Function} onDelete - Callback para eliminar (row)
 * @param {Boolean} hasEnabledToggle - Si tiene toggle de enabled
 * @param {Function} onEnabledChange - Callback para cambiar enabled (id, value)
 * @param {Object} pagination - { currentPage, totalPages, totalElements, numberOfElements }
 * @param {Function} onPageChange - Callback para cambiar página
 */
const DataGrid = ({
  columns = [],
  data = [],
  sortConfig = { key: 'id', direction: 'descending' },
  onSort,
  onEdit,
  onDelete,
  hasEnabledToggle = false,
  onEnabledChange,
  pagination,
  onPageChange,
}) => {
  const { t } = useTranslation();
  const totalPages = Math.max(1, pagination?.totalPages || 1);
  const currentPage = Math.min(Math.max(0, pagination?.currentPage || 0), totalPages - 1);
  const visiblePages = buildCompactPagination(totalPages, currentPage);

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      if (sortConfig.direction === 'ascending') {
        return <FontAwesomeIcon icon={faSortUp} />;
      } else {
        return <FontAwesomeIcon icon={faSortDown} />;
      }
    } else {
      return <FontAwesomeIcon icon={faSort} />;
    }
  };

  return (
    <>
      <div className="table-responsive">
        <table className="table table-bordered table-striped mb-none">
          <thead>
            <tr>
              {columns
                .filter(col => col.showInGrid ?? true)  // 🎉 Solo mostrar si showInGrid es true (default: true)
                .map((column) => {
                  // 🎉 Traducir label si es una clave de traducción
                  const translatedLabel = typeof column.label === 'string' && column.label.includes('.') 
                    ? t(column.label) 
                    : column.label;
                  
                  return (
                    <th
                      key={column.key}
                      onClick={column.sortable && onSort ? () => onSort(column.key) : undefined}
                      style={{ 
                        whiteSpace: 'nowrap',
                        cursor: column.sortable ? 'pointer' : 'default'
                      }}
                      className={column.headerClassName || ''}
                    >
                      {translatedLabel} {column.sortable && onSort && getSortIcon(column.key)}
                    </th>
                  );
                })}
              
              {hasEnabledToggle && (
                <th className="actions-column">{t('common.enabled')}</th>
              )}
              
              {(onEdit || onDelete) && (
                <th className="actions-column">{t('common.actions')}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan={columns.filter(col => col.showInGrid ?? true).length + (hasEnabledToggle ? 1 : 0) + (onEdit || onDelete ? 1 : 0)} style={{ textAlign: 'center', padding: '20px' }}>
                  {t('common.noData')}
                </td>
              </tr>
            ) : (
              data.map((row) => (
                <tr key={row.id}>
                  {columns
                    .filter(col => col.showInGrid ?? true)  // 🎉 Solo mostrar si showInGrid es true
                    .map((column) => (
                      <td key={`${row.id}-${column.key}`} className={column.className || ''}>
                        {autoRenderCell(row, column)}
                      </td>
                    ))}
                  
                  {hasEnabledToggle && (
                    <td className='actions-column'>
                      <input
                        type="checkbox"
                        checked={row.enabled || false}
                        onChange={(e) => onEnabledChange && onEnabledChange(row.id, e.target.checked)}
                      />
                    </td>
                  )}
                  
                  {(onEdit || onDelete) && (
                    <td className='actions-column'>
                      {onEdit && (
                        <button
                          type="button"
                          onClick={() => onEdit(row)}
                          className="grid-action-btn grid-action-btn-edit"
                          title={t('common.edit')}
                          aria-label={t('common.edit')}
                        >
                          <FontAwesomeIcon
                            icon={faPenToSquare}
                            className="action-icon action-edit"
                          />
                        </button>
                      )}
                      {onDelete && (
                        <button
                          type="button"
                          onClick={() => onDelete(row)}
                          className="grid-action-btn grid-action-btn-delete"
                          title={t('common.delete')}
                          aria-label={t('common.delete')}
                        >
                          <FontAwesomeIcon
                            icon={faTrashCan}
                            className="action-icon action-delete"
                          />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination && (
        <div className="pagination-container">
          <ul className="pagination">
            <li className={currentPage === 0 ? 'disabled' : ''}>
              <button
                type="button"
                onClick={() => onPageChange && onPageChange(0)}
                className="pagination-button"
                title={t('common.pagination.first', { defaultValue: 'Primera página' })}
                aria-label={t('common.pagination.first', { defaultValue: 'Primera página' })}
              >
                «
              </button>
            </li>
            <li className={currentPage === 0 ? 'disabled' : ''}>
              <button
                type="button"
                onClick={() => onPageChange && onPageChange(currentPage - 1)}
                className="pagination-button"
              >
                {t('common.pagination.previous')}
              </button>
            </li>
            {visiblePages.map((pageItem) => (
              <li
                key={String(pageItem)}
                className={pageItem === currentPage ? 'active' : pageItem.toString().startsWith('ellipsis') ? 'disabled' : ''}
              >
                {pageItem.toString().startsWith('ellipsis') ? (
                  <span className="pagination-button" aria-hidden="true">
                    ...
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => onPageChange && onPageChange(pageItem)}
                    className="pagination-button"
                  >
                    {pageItem + 1}
                  </button>
                )}
              </li>
            ))}
            <li className={currentPage === totalPages - 1 ? 'disabled' : ''}>
              <button
                type="button"
                onClick={() => onPageChange && onPageChange(currentPage + 1)}
                className="pagination-button"
              >
                {t('common.pagination.next')}
              </button>
            </li>
            <li className={currentPage === totalPages - 1 ? 'disabled' : ''}>
                <button
                  type="button"
                onClick={() => onPageChange && onPageChange(totalPages - 1)}
                  className="pagination-button"
                  title={t('common.pagination.last', { defaultValue: 'Última página' })}
                  aria-label={t('common.pagination.last', { defaultValue: 'Última página' })}
                >
                  »
                </button>
            </li>
          </ul>

          <div className="pagination-summary">
            <p>
              {t('common.pagination.showing', {
                count: pagination.numberOfElements,
                total: pagination.totalElements,
                page: currentPage + 1,
                totalPages
              })}
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default DataGrid;

